import { useMemo } from "react";
import { Bar, BarChart, Cell, LabelList, XAxis } from "recharts";
import { TrendingUp, TrendingDown } from "lucide-react";

import { cn } from "@/modern/utils";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/modern/components/ui/chart";
import { Avatar, AvatarImage, AvatarFallback } from "@/modern/components/ui/avatar";
import {
  useCoverage,
  type PromotionDateRange,
} from "../context/CoverageContext";

const DATE_RANGE_OPTIONS: { value: PromotionDateRange; label: string }[] = [
  { value: "7d", label: "7D" },
  { value: "14d", label: "14D" },
  { value: "1m", label: "1M" },
  { value: "all", label: "All" },
];

const chartConfig: ChartConfig = {
  displayValue: {
    label: "Promotions",
    color: "hsl(var(--chart-1))",
  },
};

// Fixed pixel geometry so bars + avatars align regardless of user count
const BAR_SIZE = 34;
const BAR_CATEGORY_GAP = 22; // px between category slots
const BAR_SLOT = BAR_SIZE + BAR_CATEGORY_GAP; // 56px per user
const CHART_MARGIN = { top: 20, right: 8, bottom: 0, left: 8 } as const;

const AVATAR_COLORS = [
  "hsl(0 72% 51%)",
  "hsl(207 77% 51%)",
  "hsl(142 76% 36%)",
  "hsl(45 97% 54%)",
  "hsl(280 67% 51%)",
  "hsl(174 86% 45%)",
  "hsl(25 95% 53%)",
  "hsl(339 82% 51%)",
];

function getAvatarColor(userId: number): string {
  return AVATAR_COLORS[userId % AVATAR_COLORS.length];
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 1).toUpperCase();
}

export const ProductivityChart = () => {
  const {
    promotionsByUser,
    promotionDateRange,
    setPromotionDateRange,
    promotionDateRangeLabel,
    totalPromotions,
    promotionsTrend,
  } = useCoverage();

  const chartData = useMemo(() => {
    const maxPromotions = Math.max(...promotionsByUser.map((u) => u.promotions), 1);
    return promotionsByUser.map((u) => ({
      name: u.name,
      promotions: u.promotions,
      userId: u.userId,
      profileImageURL: u.profileImageURL,
      opacity: u.promotions > 0 ? 0.5 + (u.promotions / maxPromotions) * 0.5 : 0.25,
      displayValue: u.promotions > 0 ? u.promotions : 0.3,
      isZero: u.promotions === 0,
    }));
  }, [promotionsByUser]);

  const trendIsPositive = promotionsTrend != null && promotionsTrend >= 0;

  return (
    <div className="flex flex-col overflow-hidden rounded-xl border border-border">
      {/* Header */}
      <div className="flex shrink-0 items-center justify-between border-b border-border bg-card px-4 pt-3.5 pb-3">
        <p className="m-0 text-base font-normal leading-none tracking-tight text-foreground">Promotions</p>
        <div className="flex items-center gap-0.5 rounded-md bg-muted p-0.5">
          {DATE_RANGE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setPromotionDateRange(opt.value)}
              className={cn(
                "rounded-[5px] px-2 py-0.5 text-2xs font-medium transition-colors",
                promotionDateRange === opt.value
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:bg-muted-foreground/15 hover:text-foreground",
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-background">
        {/* Summary */}
        <div className="flex items-baseline gap-2.5 px-4 pt-3 pb-1">
          <span className="text-3xl font-semibold tracking-tight leading-none text-foreground">
            {totalPromotions}
          </span>
          <span className="text-xs text-muted-foreground">{promotionDateRangeLabel}</span>
          {promotionsTrend != null ? (
            <span
              className={cn(
                "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-2xs font-medium",
                trendIsPositive
                  ? "bg-success/15 text-success"
                  : "bg-destructive/15 text-destructive",
              )}
            >
              {trendIsPositive ? (
                <TrendingUp className="size-3" />
              ) : (
                <TrendingDown className="size-3" />
              )}
              {trendIsPositive ? "+" : ""}
              {promotionsTrend}%
            </span>
          ) : null}
        </div>

        {/* Chart + Avatar — left-aligned, fixed slot width so both stay in sync */}
        {chartData.length > 0 ? (
          <div className="overflow-x-auto select-none">
            {(() => {
              const chartWidth =
                chartData.length * BAR_SLOT + CHART_MARGIN.left + CHART_MARGIN.right;
              return (
                <div style={{ width: chartWidth }}>
                  <div className="pt-1 pb-0.5" onMouseDown={(e) => e.preventDefault()}>
                    <ChartContainer config={chartConfig} className="aspect-auto h-[120px] w-full [&_svg]:outline-none [&_svg]:focus:outline-none">
                      <BarChart
                        data={chartData}
                        margin={CHART_MARGIN}
                        barSize={BAR_SIZE}
                        barCategoryGap={BAR_CATEGORY_GAP}
                      >
                        <XAxis dataKey="name" hide />
                        <ChartTooltip
                          cursor={false}
                          content={
                            <ChartTooltipContent
                              hideLabel
                              nameKey="name"
                              formatter={(value, _name, item) => (
                                <div className="flex w-full items-center justify-between gap-4">
                                  <span className="text-muted-foreground">{item.payload?.name}</span>
                                  <span className="font-mono font-medium tabular-nums text-foreground">
                                    {Math.round(Number(value))}
                                  </span>
                                </div>
                              )}
                            />
                          }
                        />
                        <Bar dataKey="displayValue" radius={[6, 6, 0, 0]} isAnimationActive={false}>
                          <LabelList
                            dataKey="promotions"
                            position="top"
                            className="fill-muted-foreground"
                            fontSize={11}
                            fontWeight={500}
                            offset={4}
                          />
                          {chartData.map((entry) => (
                            <Cell
                              key={entry.userId}
                              fill={entry.isZero ? "hsl(var(--muted-foreground))" : "hsl(var(--chart-1))"}
                              fillOpacity={entry.isZero ? 1 : entry.opacity}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ChartContainer>
                  </div>

                  {/* Avatar row — one slot per bar, left-aligned to match */}
                  <div
                    className="flex items-center pb-3.5 pt-0.5"
                    style={{ paddingLeft: CHART_MARGIN.left, paddingRight: CHART_MARGIN.right }}
                  >
                    {chartData.map((user) => (
                      <div
                        key={user.userId}
                        className="flex shrink-0 flex-col items-center"
                        style={{ width: BAR_SLOT }}
                      >
                        <Avatar className="h-7 w-7">
                          {user.profileImageURL ? (
                            <AvatarImage src={user.profileImageURL} alt={user.name} />
                          ) : null}
                          <AvatarFallback
                            className="text-[10px] font-semibold text-white"
                            style={{ backgroundColor: getAvatarColor(user.userId) }}
                          >
                            {getInitials(user.name)}
                          </AvatarFallback>
                        </Avatar>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        ) : (
          <div className="flex h-[120px] items-center justify-center pb-3">
            <p className="text-xs text-muted-foreground/60">No promotion data</p>
          </div>
        )}
      </div>
    </div>
  );
};
